import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import { MenuSocials, MenuSocialsMobile } from "./MenuSocials";
import { MenuCoach, MenuCoachMobile } from "./MenuCoach";
import { HashLink } from "./HashLink";
import { CONTACT_EMAIL, PHONE, STUDIO } from "../constants/contact";

export const MenuOverlay = ({ open, onClose }) => {
  const { t } = useLang();
  const links = [
    { to: "/", label: t.menu.home, tid: "menu-home-link" },
    { to: "/#experience", label: t.nav.experience, tid: "menu-experience-link" },
    { to: "/#studio", label: t.nav.studio, tid: "menu-studio-link" },
    { to: "/#schedule", label: t.nav.schedule, tid: "menu-schedule-link" },
    { to: "/#pricing", label: t.nav.pricing, tid: "menu-pricing-link" },
    { to: "/#location", label: t.nav.location, tid: "menu-location-link" },
    { to: "/book", label: t.nav.reserve, tid: "menu-reserve-link" },
  ];
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="menu-overlay"
          initial={{ clipPath: "inset(0 0 100% 0)" }}
          animate={{ clipPath: "inset(0 0 0% 0)" }}
          exit={{ clipPath: "inset(0 0 100% 0)" }}
          transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
          className="fixed inset-0 z-[60] bg-[#080808]"
        >
          <div className="h-full flex flex-col justify-between pt-32 pb-10 px-6 md:px-14 overflow-y-auto">
            <nav>
              {links.map((l, i) => (
                <motion.div
                  key={l.tid}
                  initial={{ y: 60, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.8, delay: 0.25 + i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                  className="overflow-hidden w-fit"
                >
                  {(() => {
                    const inner = (
                      <>
                        <span className="font-sans text-[0.65rem] tracking-[0.3em] text-[#C5A059]">
                          0{i + 1}
                        </span>
                        <span className="font-serif text-4xl md:text-6xl lg:text-7xl text-white leading-tight transition-all duration-500 group-hover:italic group-hover:text-[#C5A059] group-hover:pl-4">
                          {l.label}
                        </span>
                      </>
                    );
                    // `w-fit` es lo que hace que el enlace mida lo que su texto:
                    // si fuera de ancho completo, el menú reaccionaría con el
                    // cursor al otro extremo de la pantalla.
                    const cls = "group inline-flex w-fit items-baseline gap-5 py-2 md:py-3";
                    const isLanding = l.to === "/" || l.to.startsWith("/#");
                    return isLanding ? (
                      <HashLink id={l.to === "/" ? "/" : l.to.slice(1)} data-testid={l.tid} onClick={onClose} className={cls}>
                        {inner}
                      </HashLink>
                    ) : (
                      <Link to={l.to} data-testid={l.tid} onClick={onClose} className={cls}>
                        {inner}
                      </Link>
                    );
                  })()}
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-t border-white/10 pt-8"
            >
              <div className="text-xs text-zinc-500 font-light space-y-1">
                <p className="overline-gold mb-3">{t.footer.hours}</p>
                <p>{t.footer.hoursWeek}</p>
                <p>{t.footer.hoursWeekend}</p>
                <MenuCoachMobile />
              </div>
              <div className="text-xs text-zinc-500 font-light space-y-1 md:text-right">
                <p className="overline-gold mb-3">{t.footer.contact}</p>
                <a href={`mailto:${CONTACT_EMAIL}`} className="block hover:text-[#C5A059] transition-colors duration-300">
                  {CONTACT_EMAIL}
                </a>
                <a href={`tel:${PHONE.tel}`} className="block hover:text-[#C5A059] transition-colors duration-300">
                  {PHONE.display}
                </a>
                <p>
                  {STUDIO.addressLines.en[0]}
                  <br />
                  {STUDIO.addressLines.en[1]}
                </p>
                <MenuSocialsMobile />
              </div>
            </motion.div>
          </div>
          <MenuCoach />
          <MenuSocials />
        </motion.div>
      )}
    </AnimatePresence>
  );
};
